/**
 * @fileoverview Fetching a page's bytes from anywhere, and refusing to believe
 * any of it until the hash matches.
 *
 * THE VERIFICATION IS THE WHOLE POINT. The index event names a hash and a list
 * of places to try, never one URL. That is what makes leaving, self-hosting and
 * mirroring work: a reader can pull the bytes from a server nobody trusts,
 * including one the author has never heard of, because the bytes either hash to
 * the name in the event or they are discarded. A renderer that skips the check
 * has thrown that away and will render whatever a hostile mirror hands back --
 * with the author's name on it.
 *
 * So the check is not a late guard on a happy path. `fetchVerifiedBlob` is the
 * ONLY way to get bytes in this app, and it has no option to skip.
 */

const HEX_64 = /^[0-9a-f]{64}$/;

/** Hex-encode a digest. */
function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  return toHex(await crypto.subtle.digest('SHA-256', bytes));
}

/**
 * Build the ordered list of URLs to try for one blob.
 *
 * Order: the page's own server hints, then the author's BUD-03 declared list,
 * then our fallbacks. Each source is more specific about THIS page than the
 * next, and the last is only a courtesy -- the bytes are verified whichever
 * answers, so the order is about latency and politeness, not about trust.
 *
 * BUD-01 addresses a blob at `GET /<sha256>`. Servers that also serve
 * `/<sha256>.<ext>` redirect to it, and a redirect is cheaper than us guessing
 * an extension we do not reliably know.
 */
export function blobUrls(
  hash: string,
  sources: { pageHints: string[]; authorServers: string[]; fallbacks: readonly string[] }
): string[] {
  const seen = new Set<string>();
  const urls: string[] = [];
  for (const base of [...sources.pageHints, ...sources.authorServers, ...sources.fallbacks]) {
    let url: string;
    try {
      url = new URL(`/${hash}`, base).toString();
    } catch {
      continue; // A malformed server hint is one bad tag, not a broken page.
    }
    if (!seen.has(url)) {
      seen.add(url);
      urls.push(url);
    }
  }
  return urls;
}

/**
 * Try each URL in turn and return the first response whose bytes hash to
 * `hash`. Returns null when nothing does.
 *
 * NULL, NOT AN EXCEPTION, and no indication of which URLs were tried or how
 * they failed. Every failure mode of a page load -- missing event, dead server,
 * tampered bytes, ciphertext -- has to reach the reader identically, because a
 * private page must look exactly like a missing one to a stranger holding its
 * address. A richer error type here is precisely what would leak the
 * difference.
 *
 * A HASH MISMATCH IS NOT A FAILURE OF THE FETCH, it is a rejection of that
 * server, so the loop moves on rather than giving up: one bad mirror in a list
 * of four must not cost the reader the page.
 */
export async function fetchVerifiedBlob(
  hash: string,
  urls: string[],
  opts: { timeoutMs: number; fetchImpl?: typeof fetch } = { timeoutMs: 8000 }
): Promise<ArrayBuffer | null> {
  if (!HEX_64.test(hash)) return null;
  const doFetch = opts.fetchImpl ?? fetch;

  for (const url of urls) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), opts.timeoutMs);
    try {
      const response = await doFetch(url, { signal: controller.signal, redirect: 'follow' });
      if (!response.ok) continue;
      const bytes = await response.arrayBuffer();
      if ((await sha256Hex(bytes)) === hash) return bytes;
      // Wrong bytes under the right name. Silently skip: naming the server in a
      // user-visible error would turn a mirror's misbehaviour into a claim we
      // are making about a third party, and we have no more evidence than "it
      // did not match".
    } catch {
      // Network error, CORS refusal, abort. Next source.
    } finally {
      clearTimeout(timer);
    }
  }
  return null;
}

/** Decode verified bytes as UTF-8 text. */
export function decodeUtf8(bytes: ArrayBuffer): string {
  return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
}
